import React from 'react';
import { JobsitePulseCamera } from 'containers/jobsite/tabs/jobsitePulse/data/types';
import { useJobsitePulse } from 'containers/jobsite/tabs/jobsitePulse/components/JobsitePulseProvider';
import { DroneDeployPhase } from './types';
import { DEMO_DRONE_CAMERA } from './droneDemoData';

export type DroneDeployContextType = {
  phase: DroneDeployPhase;
  coordinates: string;
  deploy: (coordinates: string) => void;
  setReady: () => void;
  reset: () => void;
  /** The currently selected camera (drone or regular). */
  selectedCamera: JobsitePulseCamera | null;
  /** Selects a camera by ID — handles both drone and regular cameras. */
  selectCamera: (cameraId?: string | null) => void;
};

const DRONE_IDS = new Set([DEMO_DRONE_CAMERA.id]);

const DroneDeployContext = React.createContext<DroneDeployContextType | null>(
  null,
);

export function useDroneDeploy(): DroneDeployContextType | null {
  return React.useContext(DroneDeployContext);
}

export function DroneDeployProvider(
  props: React.PropsWithChildren,
): React.ReactElement {
  const { children } = props;
  const {
    selectCamera: pulseSelectCamera,
    selectedCamera: pulseSelectedCamera,
  } = useJobsitePulse();

  const [selectedDroneId, setSelectedDroneId] = React.useState<string | null>(
    null,
  );

  const [state, setState] = React.useState<{
    phase: DroneDeployPhase;
    coordinates: string;
  }>({ phase: 'map', coordinates: '' });

  const selectCamera = React.useCallback(
    (cameraId?: string | null) => {
      if (cameraId && DRONE_IDS.has(cameraId)) {
        setSelectedDroneId(cameraId);
        pulseSelectCamera(null);
      } else {
        setSelectedDroneId(null);
        pulseSelectCamera(cameraId);
      }
    },
    [pulseSelectCamera],
  );

  const selectedCamera = selectedDroneId
    ? DEMO_DRONE_CAMERA
    : pulseSelectedCamera;

  // Reset deploy phase when selected camera changes
  React.useEffect(() => {
    setState({ phase: 'map', coordinates: '' });
  }, [selectedCamera?.id]);

  const deploy = React.useCallback((coordinates: string) => {
    setState({ phase: 'deploying', coordinates });
  }, []);

  const setReady = React.useCallback(() => {
    setState((prev) => ({ ...prev, phase: 'ready' }));
  }, []);

  const reset = React.useCallback(() => {
    setState({ phase: 'map', coordinates: '' });
  }, []);

  const value = React.useMemo(
    (): DroneDeployContextType => ({
      ...state,
      deploy,
      setReady,
      reset,
      selectedCamera,
      selectCamera,
    }),
    [state, deploy, setReady, reset, selectedCamera, selectCamera],
  );

  return (
    <DroneDeployContext.Provider value={value}>
      {children}
    </DroneDeployContext.Provider>
  );
}
